import { getCommonTimezones, getTimezoneOffsetMinutes, formatTimezoneOffset } from './timezone'

export interface DynamicTimezone {
    value: string
    label: string
    currentOffset: string
    offsetMinutes: number
}

/**
 * Get dynamic timezone list with current offsets (including DST), sorted by offset
 */
export function getDynamicTimezones(): DynamicTimezone[] {
    const commonTimezones = getCommonTimezones()
    const timezones = commonTimezones.map(tz => {
        try {
            const offsetMinutes = getTimezoneOffsetMinutes(tz.value)
            const offsetString = formatTimezoneOffset(offsetMinutes)

            return {
                value: tz.value,
                label: `${tz.label} (${offsetString})`,
                currentOffset: offsetString,
                offsetMinutes
            }
        } catch (error) {
            console.warn(`Failed to get offset for timezone ${tz.value}:`, error)
            return {
                value: tz.value,
                label: tz.label,
                currentOffset: 'UTC+00:00',
                offsetMinutes: 0
            }
        }
    })

    // Sort by offset (UTC-12 to UTC+14), then by name for same offset
    return timezones.sort((a, b) => {
        if (a.offsetMinutes !== b.offsetMinutes) {
            return a.offsetMinutes - b.offsetMinutes
        }
        return a.label.localeCompare(b.label)
    })
}

/**
 * Get timezone info for a specific timezone
 */
export function getTimezoneInfo(timezone: string): DynamicTimezone | null {
    const timezones = getDynamicTimezones()
    return timezones.find(tz => tz.value === timezone) || null
}

/**
 * Convert UTC time to local time using dynamic offset
 */
export function convertUTCToLocalTime(utcDate: Date | string, timezone: string): Date {
    try {
        const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate

        // Calculate offset for this specific date (handles DST)
        const utc = new Date(dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000))
        const targetTime = new Date(utc.toLocaleString('en-US', { timeZone: timezone }))
        const offsetMs = targetTime.getTime() - utc.getTime()
        const offsetMinutes = Math.round(offsetMs / 60000) // Round to avoid decimal places

        // Apply offset to convert from UTC to local time
        return new Date(dateObj.getTime() + (offsetMinutes * 60000))
    } catch (error) {
        console.warn('Failed to convert UTC to local time:', error)
        return typeof utcDate === 'string' ? new Date(utcDate) : utcDate
    }
}

/**
 * Format date in specific timezone with dynamic offset
 */
export function formatDateInTimezone(
    date: Date | string,
    timezone: string,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
    }
): string {
    // Declared OUTSIDE the try so the catch can fall back to it — see the
    // identical fix in timezone.ts.
    const dateObj = typeof date === 'string' ? new Date(date) : date
    try {
        return new Intl.DateTimeFormat('en-US', {
            ...options,
            timeZone: timezone
        }).format(dateObj)
    } catch (error) {
        console.warn('Failed to format date in timezone:', error)
        return dateObj.toISOString()
    }
}
